import { useState, useEffect } from 'react';
import { Tab, Box } from '@mui/material';
import { TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Alert } from '@mui/material';
import { SafeTable, SafeTableCell } from 'components/shared/safe/SafeTable';
import { TabContext, TabList, TabPanel } from '@mui/lab';
// @ts-ignore
//import SwipeableViews from 'react-swipeable-views-react-18-fix';

import { Activity, ActivityItem } from "interfaces/ActivityInterfaces";
import { Check as CheckIcon, Close as CloseIcon, Checklist as ChecklistIcon } from '@mui/icons-material';
import { useBusiness } from 'hooks/useBusiness';
import { useLocalization } from 'hooks/useLocalization';

interface ActivityItemTabsProps {
	activity: Activity;
}

function ActivityItemTabs({ activity }: ActivityItemTabsProps) {
	const { activityItems } = useBusiness();
	const { getStr } = useLocalization();

	const tabTypes = [
		{ value: 'included', label: getStr('TAB_included'), icon: <CheckIcon /> },
		{ value: 'notIncluded', label: getStr('TAB_notIncluded'), icon: <CloseIcon /> },
		{ value: 'whatToBring', label: getStr('TAB_whatToBring'), icon: <ChecklistIcon /> }
	];

	// Determine the default tab and its index based on the activity's items
	const getDefaultTabAndIndex = () => {
		const defaultTabIndex = tabTypes.findIndex(tabType => 
		activity.itemIds[tabType.value as keyof typeof activity.itemIds]?.length > 0
		);
		return {
		defaultTab: defaultTabIndex !== -1 ? tabTypes[defaultTabIndex].value : tabTypes[0].value,
		defaultTabIndex: defaultTabIndex !== -1 ? defaultTabIndex : 0,
		};
	};

	const { defaultTab, defaultTabIndex } = getDefaultTabAndIndex();
	
	const [selectedTab, setSelectedTab] = useState<string>(defaultTab);
	const [index, setIndex] = useState<number>(defaultTabIndex);

	const handleChange = (event: React.SyntheticEvent, newValue: string) => {
		setSelectedTab(newValue);
		const newIndex = tabTypes.findIndex(tabType => tabType.value === newValue);
		setIndex(newIndex !== -1 ? newIndex : 0);
	};

	const handleChangeIndex = (index: number) => {
		setIndex(index);
		if (tabTypes[index]) {
			setSelectedTab(tabTypes[index].value);
		}
	};

	const shouldDisplayImageCell = (itemIds: number[], activityItems: ActivityItem[]): boolean => {
		return itemIds.some(itemId => {
		  const item = activityItems.find(item => item.id === itemId);
		  return !!item?.imageUrl;
		});
	  };

	if (activity.itemIds.included.length === 0 && activity.itemIds.notIncluded.length === 0 && activity.itemIds.whatToBring.length === 0) {
		return null;
	}

	return (
		<Box sx={{ width: '100%',
			borderColor: 'divider' }}
		>
		<TabContext value={selectedTab}>
			<TabList
				onChange={handleChange}
				aria-label="basic tabs example"
			>
				{tabTypes.map((tabType) => activity.itemIds[tabType.value as keyof typeof activity.itemIds]?.length > 0 && (
					<Tab
						key={tabType.value}
						value={tabType.value}
						label={
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
								{tabType.icon}
								{tabType.label}
							</Box>
						}
					/>
				))}
			</TabList>
			{tabTypes.map((tabType) => {
				const itemIdsInCategory = activity.itemIds[tabType.value as keyof typeof activity.itemIds] || [];
				const displayImageCell = shouldDisplayImageCell(itemIdsInCategory, activityItems);

				return (
					<TabPanel
						key={tabType.value}
						value={tabType.value}
						sx={{ backgroundColor: '#f5f5f5' }}
					>
						<TableContainer>
							<SafeTable>
								<TableBody>
									{
										itemIdsInCategory.map(activityItemId => {
											const item = activityItems.find(item => item.id === activityItemId);
											
											return item ? (
												<TableRow key={item.id}>
													{ displayImageCell ? (
														<SafeTableCell sx={{ borderBottom: 0, width: '60px' }}>
															{ item.imageUrl ? (<img src={item.imageUrl} style={{ width: '60px', minWidth: '60px', height: '60px', minHeight: '60px' }} alt={item.title} />) : null }
														</SafeTableCell>
													) : null }
													<SafeTableCell sx={{ borderBottom: 0, fontSize: '1em' }}>
														{
															item.title ? (
																<span style={{ fontWeight: 'bold' }}>{item.title}</span>
															) : <><span className="tt-error-line">{getStr('Name_unset')}</span></>
														}
														{
															item.desc ? (
																<div className="tt-description-line">
																	{item.desc}
																</div>
															) : null
														}
													</SafeTableCell>
												</TableRow>
											) : null;
										})
									}
								</TableBody>
							</SafeTable>
						</TableContainer>
					</TabPanel>
				)
			})}
		</TabContext>
	</Box>
	);
}

export default ActivityItemTabs;